import React, { useState } from 'react'
import { CheckCircle, Clock, AlertCircle, FileText, Building2, User, FileCheck } from 'lucide-react'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { ExchangeWizardHub } from '../components/wizard/ExchangeWizardHub'

interface Activity {
  id: number
  type: 'incomplete' | 'pending' | 'reminder' | 'completed'
  title: string
  description: string
  timestamp: string
  urgent?: boolean
  action?: () => void
}

export const ActivityPage: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isWizardOpen, setIsWizardOpen] = useState(false)
  const [wizardInitialTab, setWizardInitialTab] = useState<'initial' | 'exchange' | 'acquisition'>('initial')

  const activities: Activity[] = [
    {
      id: 1,
      type: 'incomplete',
      title: 'Complete Initial Setup',
      description: 'Finish entering your relinquished property details',
      timestamp: '2 hours ago',
      urgent: true,
      action: () => {
        setWizardInitialTab('initial')
        setIsWizardOpen(true)
      }
    },
    {
      id: 2,
      type: 'pending',
      title: 'Upload Documents',
      description: '3 documents still need to be uploaded',
      timestamp: '5 hours ago',
      urgent: true,
      action: () => navigate('/app/documents')
    },
    {
      id: 3,
      type: 'reminder',
      title: 'Identity Verification',
      description: 'Complete your identity verification for faster processing',
      timestamp: '1 day ago',
      urgent: false,
      action: () => navigate('/app/profile')
    },
    {
      id: 4,
      type: 'completed',
      title: 'Exchange Agreement Signed',
      description: 'You signed the exchange agreement',
      timestamp: '2 days ago',
      urgent: false
    },
    {
      id: 5,
      type: 'completed',
      title: 'Property Sale Date Set',
      description: 'Property sale date has been recorded',
      timestamp: '3 days ago',
      urgent: false
    }
  ]

  const getActivityIcon = (type: Activity['type']) => {
    switch (type) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
      case 'incomplete':
      case 'pending':
        return <AlertCircle className="h-5 w-5 text-orange-500 dark:text-orange-400" />
      case 'reminder':
        return <Clock className="h-5 w-5 text-blue-500 dark:text-blue-400" />
      default:
        return <Clock className="h-5 w-5 text-slate-400" />
    }
  }

  const getActivityBadge = (type: Activity['type'], urgent?: boolean) => {
    if (type === 'completed') {
      return <span className="px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-xs font-semibold">Completed</span>
    }
    if (urgent) {
      return <span className="px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-xs font-semibold">Urgent</span>
    }
    return <span className="px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-semibold">Pending</span>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-2">
        <h1 className="text-2xl font-bold text-secondary-900 dark:text-slate-100 flex items-center gap-3">
          <Clock className="h-7 w-7 text-primary-600 dark:text-primary-400" />
          Recent Activity
        </h1>
        <p className="text-secondary-600 dark:text-slate-400 mt-1">View your recent exchange activities and tasks</p>
      </div>

      {/* Activities List */}
      <Card className="p-6">
        <div className="space-y-4">
          {activities.map((activity) => (
            <div
              key={activity.id}
              onClick={activity.action}
              className={`flex items-start space-x-4 p-4 rounded-lg border transition-all duration-200 ${
                activity.action
                  ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:border-primary-300 dark:hover:border-primary-700 border-slate-200 dark:border-slate-700'
                  : 'border-slate-200 dark:border-slate-700'
              }`}
            >
              <div className="flex-shrink-0 mt-0.5">
                {getActivityIcon(activity.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="text-sm font-semibold text-secondary-900 dark:text-slate-100">
                    {activity.title}
                  </h3>
                  {getActivityBadge(activity.type, activity.urgent)}
                </div>
                <p className="text-sm text-secondary-600 dark:text-slate-400 mb-2">
                  {activity.description}
                </p>
                <p className="text-xs text-secondary-500 dark:text-slate-500">
                  {activity.timestamp}
                </p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Exchange Wizard Modal */}
      {isWizardOpen && (
        <ExchangeWizardHub
          onClose={() => setIsWizardOpen(false)}
          initialTab={wizardInitialTab}
        />
      )}
    </div>
  )
}

